import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Campaign } from './schemas/schema';
import { AdminReport } from './schemas/legacyAdminSchema';
import { PAGE_SIZES } from '@features/campaign-approvals';

export type CampaignType = 'lci3' | 'cpg' | 'rsl';
export type CheckedCampaign = Pick<
  Campaign,
  'id' | 'reportGroup' | 'customReportId'
>;

type InitialState = {
  checkedCampaigns: CheckedCampaign[];
  checkedReports: AdminReport[];
  selectedAccountManager: number | undefined;
  selectedPod: number | undefined;
  selectedManagerFilter: string | undefined;
  searchText: string | undefined;
  selectedReportDate: string | undefined;
  currentPage: number;
  pageSize: number;
  showArchived: boolean;
  campaignType: CampaignType;
};

const initialState: InitialState = {
  checkedCampaigns: [],
  checkedReports: [],
  selectedAccountManager: undefined,
  selectedPod: undefined,
  selectedManagerFilter: undefined,
  searchText: undefined,
  selectedReportDate: undefined,
  currentPage: 1,
  pageSize: PAGE_SIZES[0],
  showArchived: false,
  campaignType: 'lci3',
};

export const approvalsSlice = createSlice({
  name: 'approvalsSlice',
  initialState,
  reducers: {
    campaignSelected(
      state,
      {
        payload: { campaign, reports },
      }: PayloadAction<{
        campaign: CheckedCampaign;
        reports: AdminReport[];
      }>
    ) {
      if (
        state.checkedCampaigns.some(
          c =>
            c.id === campaign.id &&
            c.reportGroup === campaign.reportGroup &&
            c.customReportId === campaign.customReportId
        )
      ) {
        state.checkedCampaigns = state.checkedCampaigns.filter(
          c =>
            c.id !== campaign.id ||
            c.reportGroup !== campaign.reportGroup ||
            c.customReportId !== campaign.customReportId
        );
        state.checkedReports = state.checkedReports.filter(
          report => !reports.map(r => r.id).includes(report.id)
        );
      } else {
        state.checkedCampaigns = [...state.checkedCampaigns, campaign];
        state.checkedReports = [
          ...new Set([...state.checkedReports, ...reports]),
        ];
      }
    },

    reportSelected(
      state,
      { payload: { report } }: PayloadAction<{ report: AdminReport }>
    ) {
      state.checkedReports = state.checkedReports
        .map(r => r.id)
        .includes(report.id)
        ? state.checkedReports.filter(c => c.id !== report.id)
        : [...state.checkedReports, report];
    },

    accountManagerSelected(
      state,
      {
        payload: { accountManagerId },
      }: PayloadAction<{ accountManagerId: number | undefined }>
    ) {
      state.selectedAccountManager = accountManagerId;
      state.selectedManagerFilter = undefined;
      state.selectedPod = undefined;
      state.currentPage = 1;
    },

    podSelected(
      state,
      { payload: { podId } }: PayloadAction<{ podId: number }>
    ) {
      state.selectedPod = podId;
      state.selectedManagerFilter = undefined;
      state.selectedAccountManager = undefined;
      state.currentPage = 1;
    },

    managerFilterSelected(state, { payload }: PayloadAction<string>) {
      state.selectedPod = undefined;
      state.selectedAccountManager = undefined;
      state.currentPage = 1;
      state.selectedManagerFilter = payload;
    },

    searchTextEntered(state, { payload }: PayloadAction<string | undefined>) {
      state.searchText = payload;
      state.currentPage = 1;
    },

    reportDateSelected(state, { payload }: PayloadAction<string | undefined>) {
      state.selectedReportDate = payload;
      state.currentPage = 1;
    },

    pageIncremented(state) {
      state.currentPage += 1;
    },

    pageDecremented(state) {
      state.currentPage -= 1;
    },

    pageSizeSelected(state, { payload }: PayloadAction<number>) {
      state.pageSize = payload;
      state.currentPage = 1;
    },

    archiveToggled(state) {
      state.showArchived = !state.showArchived;
      state.currentPage = 1;
    },

    campaignTypeSelected(state, { payload }: PayloadAction<CampaignType>) {
      state.campaignType = payload;
      state.currentPage = 1;
    },

    resetCheckedItems(state) {
      state.checkedCampaigns = [];
      state.checkedReports = [];
    },
  },
});

export const {
  campaignSelected,
  reportSelected,
  accountManagerSelected,
  podSelected,
  managerFilterSelected,
  searchTextEntered,
  reportDateSelected,
  pageIncremented,
  pageDecremented,
  pageSizeSelected,
  archiveToggled,
  campaignTypeSelected,
  resetCheckedItems,
} = approvalsSlice.actions;
